import { useCallback, useEffect, useRef, useState } from 'react';
import { ColumnDefinition } from './types';

const clampWidth = (value: number, min: number, max: number) => Math.min(Math.max(Math.round(value), min), max);

type UseColumnWidthsResult<TKey extends string> = {
    getWidth: (key: TKey) => number;
    commitWidth: (key: TKey, width: number) => void;
    minWidth: number;
    maxWidth: number;
};

type UseColumnWidthsOptions<TKey extends string> = {
    cacheKey: string;
    initialWidths: Partial<Record<TKey, number>>;
    onPersist: (widths: Record<string, number>) => void;
    persistDelayMs?: number;
    columns: Record<TKey, ColumnDefinition<TKey>>;
    minWidth?: number;
    maxWidth?: number;
};

const DEFAULT_PERSIST_DELAY_MS = 3000;

export const useColumnWidths = <TKey extends string>({
    cacheKey,
    initialWidths,
    onPersist,
    persistDelayMs = DEFAULT_PERSIST_DELAY_MS,
    columns,
    minWidth = 60,
    maxWidth = 800,
}: UseColumnWidthsOptions<TKey>): UseColumnWidthsResult<TKey> => {
    const [overrides, setOverrides] = useState<Partial<Record<TKey, number>>>({});
    const overridesRef = useRef<Partial<Record<TKey, number>>>({});
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onPersistRef = useRef(onPersist);
    const initialWidthsRef = useRef(initialWidths);

    useEffect(() => {
        onPersistRef.current = onPersist;
    }, [onPersist]);

    useEffect(() => {
        initialWidthsRef.current = initialWidths;
    }, [initialWidths]);

    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setOverrides({});
        overridesRef.current = {};
    }, [cacheKey]);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const getWidth = useCallback(
        (key: TKey) => {
            const override = overrides[key];
            if (typeof override === 'number') return override;
            const fromInitial = initialWidths[key];
            if (typeof fromInitial === 'number') return fromInitial;
            const definition = columns[key];
            return definition?.defaultWidth ?? minWidth;
        },
        [overrides, initialWidths, columns, minWidth],
    );

    const commitWidth = useCallback(
        (key: TKey, width: number) => {
            const definition = columns[key];
            const colMin = definition?.minWidth ?? minWidth;
            const colMax = definition?.maxWidth ?? maxWidth;
            const clamped = clampWidth(width, colMin, colMax);

            const next = { ...overridesRef.current, [key]: clamped } as Partial<Record<TKey, number>>;
            overridesRef.current = next;
            setOverrides(next);

            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                const merged: Record<string, number> = {};
                for (const [k, v] of Object.entries(initialWidthsRef.current)) {
                    if (typeof v === 'number') merged[k] = v;
                }
                for (const [k, v] of Object.entries(overridesRef.current)) {
                    if (typeof v === 'number') merged[k] = v;
                }
                onPersistRef.current(merged);
            }, persistDelayMs);
        },
        [columns, minWidth, maxWidth, persistDelayMs],
    );

    return { getWidth, commitWidth, minWidth, maxWidth };
};
